// Drawing Types and Interfaces for AdvancedChart

export interface Point {
  time: number
  price: number
}

export interface DrawingStyle {
  lineColor: string
  lineWidth: number
  lineStyle: 'solid' | 'dashed' | 'dotted'
  backgroundColor?: string
  textColor: string
  fontSize: number
  extend?: {
    left: boolean
    right: boolean
  }
}

export type DrawingType = 
  | 'trendline'
  | 'horizontal'
  | 'vertical'
  | 'fibonacci-retracement'
  | 'fibonacci-extension'
  | 'channel'
  | 'parallel-channel'
  | 'rectangle'
  | 'text'
  | 'head-and-shoulders'
  | 'double-top'
  | 'double-bottom'
  | 'ascending-triangle'
  | 'descending-triangle'
  | 'symmetrical-triangle'
  | 'rising-wedge'
  | 'falling-wedge'
  | 'bull-flag'
  | 'bear-flag'
  | 'pennant'

// Base Drawing Interface
export interface BaseDrawing {
  id: string
  type: DrawingType
  points: Point[]
  style: DrawingStyle
  isSelected: boolean
  isVisible: boolean
  createdAt: number
  updatedAt: number
  zIndex: number
}

// Fibonacci Retracement specific properties
export interface FibonacciLevel {
  ratio: number
  price: number
  label: string
  isVisible: boolean
  color?: string
}

export interface FibonacciRetracement extends BaseDrawing {
  type: 'fibonacci-retracement'
  levels: FibonacciLevel[]
  showPrices: boolean
  showLabels: boolean
  showBackground: boolean
  reverse: boolean
  extend: {
    left: boolean
    right: boolean
  }
}

// Fibonacci Extension specific properties
export interface FibonacciExtension extends BaseDrawing {
  type: 'fibonacci-extension'
  levels: FibonacciLevel[]
  showPrices: boolean
  showLabels: boolean
  showBackground: boolean
  basePoints: [Point, Point] // AB move
  extensionPoint: Point // C point for ABC pattern
}

// Channel specific properties
export interface Channel extends BaseDrawing {
  type: 'channel' | 'parallel-channel'
  showMidline: boolean
  channelWidth?: number // For parallel channels
  extend: {
    left: boolean
    right: boolean
  }
}

// Text annotation specific properties
export interface TextAnnotation extends BaseDrawing {
  type: 'text'
  text: string
  fontSize: number
  fontWeight: 'normal' | 'bold'
  textAlign: 'left' | 'center' | 'right'
  backgroundColor?: string
  borderColor?: string
  padding: number
}

// Pattern Recognition specific interfaces
export interface PatternPoint extends Point {
  role: 'peak' | 'valley' | 'support' | 'resistance' | 'neckline' | 'breakout'
  significance: number // 0-1 rating of how significant this point is
}

// Head and Shoulders pattern
export interface HeadAndShouldersPattern extends BaseDrawing {
  type: 'head-and-shoulders'
  patternPoints: {
    leftShoulder: PatternPoint
    head: PatternPoint
    rightShoulder: PatternPoint
    leftValley: PatternPoint
    rightValley: PatternPoint
    neckline: [PatternPoint, PatternPoint]
  }
  projectedTarget?: number
  isInverted: boolean
  confirmation: boolean
  volume?: {
    leftShoulder: number
    head: number
    rightShoulder: number
  }
}

// Double Top/Bottom pattern
export interface DoubleTopBottomPattern extends BaseDrawing {
  type: 'double-top' | 'double-bottom'
  patternPoints: {
    firstPeak: PatternPoint
    secondPeak: PatternPoint
    valley: PatternPoint // The low between peaks (for double top) or high between valleys (for double bottom)
    neckline: [PatternPoint, PatternPoint]
  }
  projectedTarget?: number
  confirmation: boolean
  tolerance: number // How much variance allowed between the two peaks/bottoms (in %)
}

// Triangle patterns (Ascending, Descending, Symmetrical)
export interface TrianglePattern extends BaseDrawing {
  type: 'ascending-triangle' | 'descending-triangle' | 'symmetrical-triangle'
  patternPoints: {
    highs: PatternPoint[] // Series of highs forming the upper trendline
    lows: PatternPoint[] // Series of lows forming the lower trendline
    convergencePoint: Point // Where the two trendlines meet
    breakoutPoint?: PatternPoint
  }
  upperTrendline: [Point, Point]
  lowerTrendline: [Point, Point]
  projectedTarget?: number
  volume?: number[] // Volume for each swing
  confirmation: boolean
  duration: number // Pattern duration in time units
}

// Wedge patterns (Rising, Falling)
export interface WedgePattern extends BaseDrawing {
  type: 'rising-wedge' | 'falling-wedge'
  patternPoints: {
    highs: PatternPoint[] // Series of higher highs (rising) or lower highs (falling)
    lows: PatternPoint[] // Series of higher lows (rising) or lower lows (falling)
    convergencePoint: Point
    breakoutPoint?: PatternPoint
  }
  upperTrendline: [Point, Point]
  lowerTrendline: [Point, Point]
  projectedTarget?: number
  volume?: number[] // Typically declining volume in wedges
  confirmation: boolean
  slope: {
    upper: number // Slope of upper trendline
    lower: number // Slope of lower trendline
  }
}

// Flag and Pennant patterns
export interface FlagPennantPattern extends BaseDrawing {
  type: 'bull-flag' | 'bear-flag' | 'pennant'
  patternPoints: {
    poleStart: PatternPoint // Start of the flagpole
    poleEnd: PatternPoint // End of flagpole/start of consolidation
    consolidationHigh: PatternPoint
    consolidationLow: PatternPoint
    breakoutPoint?: PatternPoint
  }
  flagpole: {
    start: Point
    end: Point
    height: number // Price distance of the pole
  }
  consolidation: {
    upperBound: [Point, Point]
    lowerBound: [Point, Point]
    duration: number
  }
  projectedTarget?: number
  volume?: {
    poleVolume: number // High volume during pole formation
    flagVolume: number // Lower volume during consolidation
    breakoutVolume?: number // Volume on breakout
  }
  confirmation: boolean
}

// Union type for all pattern types
export type PatternDrawing = 
  | HeadAndShouldersPattern
  | DoubleTopBottomPattern
  | TrianglePattern
  | WedgePattern
  | FlagPennantPattern

// Updated main Drawing union to include patterns
export type Drawing = 
  | BaseDrawing
  | FibonacciRetracement
  | FibonacciExtension
  | Channel
  | TextAnnotation
  | PatternDrawing

// Default Fibonacci levels based on TradingView standards
export const DEFAULT_FIBONACCI_RETRACEMENT_LEVELS: FibonacciLevel[] = [
  { ratio: 0, price: 0, label: '0%', isVisible: true, color: '#787B86' },
  { ratio: 0.236, price: 0, label: '23.6%', isVisible: true, color: '#F23645' },
  { ratio: 0.382, price: 0, label: '38.2%', isVisible: true, color: '#FF9800' },
  { ratio: 0.5, price: 0, label: '50%', isVisible: true, color: '#4CAF50' },
  { ratio: 0.618, price: 0, label: '61.8%', isVisible: true, color: '#9C27B0' },
  { ratio: 0.786, price: 0, label: '78.6%', isVisible: true, color: '#2196F3' },
  { ratio: 1, price: 0, label: '100%', isVisible: true, color: '#787B86' }
]

export const DEFAULT_FIBONACCI_EXTENSION_LEVELS: FibonacciLevel[] = [
  { ratio: 0, price: 0, label: '0%', isVisible: true, color: '#787B86' },
  { ratio: 0.618, price: 0, label: '61.8%', isVisible: true, color: '#9C27B0' },
  { ratio: 1, price: 0, label: '100%', isVisible: true, color: '#787B86' },
  { ratio: 1.272, price: 0, label: '127.2%', isVisible: true, color: '#F23645' },
  { ratio: 1.414, price: 0, label: '141.4%', isVisible: true, color: '#FF9800' },
  { ratio: 1.618, price: 0, label: '161.8%', isVisible: true, color: '#4CAF50' },
  { ratio: 2.618, price: 0, label: '261.8%', isVisible: true, color: '#2196F3' }
]

// Drawing tool configuration
export interface DrawingToolConfig {
  snapToPrice: boolean
  snapTolerance: number
  showTooltips: boolean
  enableMagnetMode: boolean
  defaultStyle: DrawingStyle
}

// Drawing Manager state
export interface DrawingState {
  drawings: Map<string, Drawing>
  activeDrawing: Drawing | null
  selectedDrawings: Set<string>
  activeTool: DrawingType | 'select'
  isDrawing: boolean
  history: Drawing[][]
  historyIndex: number
  config: DrawingToolConfig
}

// Drawing events
export type DrawingEvent = 
  | { type: 'start'; point: Point }
  | { type: 'update'; point: Point }
  | { type: 'complete'; drawing: Drawing }
  | { type: 'select'; drawingId: string }
  | { type: 'deselect'; drawingId?: string }
  | { type: 'delete'; drawingId: string }
  | { type: 'modify'; drawingId: string; changes: Partial<Drawing> }

// Helper functions for drawing calculations
export const calculateFibonacciPrices = (
  startPoint: Point,
  endPoint: Point,
  levels: FibonacciLevel[],
  reverse: boolean = false
): FibonacciLevel[] => {
  const priceDifference = endPoint.price - startPoint.price
  
  return levels.map(level => ({
    ...level,
    price: reverse
      ? endPoint.price - (level.ratio * priceDifference)
      : startPoint.price + (level.ratio * priceDifference)
  }))
}

export const calculateFibonacciExtensionPrices = (
  pointA: Point,
  pointB: Point,
  pointC: Point,
  levels: FibonacciLevel[]
): FibonacciLevel[] => {
  const abMove = pointB.price - pointA.price
  
  return levels.map(level => ({
    ...level,
    price: pointC.price + (level.ratio * abMove)
  }))
}

export const calculateChannelLines = (
  startPoint: Point,
  endPoint: Point,
  width?: number
): { upperLine: [Point, Point], lowerLine: [Point, Point], midLine?: [Point, Point] } => {
  if (width) {
    // Parallel channel with fixed width
    const upperLine: [Point, Point] = [
      { ...startPoint, price: startPoint.price + width / 2 },
      { ...endPoint, price: endPoint.price + width / 2 }
    ]
    const lowerLine: [Point, Point] = [
      { ...startPoint, price: startPoint.price - width / 2 },
      { ...endPoint, price: endPoint.price - width / 2 }
    ]
    const midLine: [Point, Point] = [startPoint, endPoint]
    
    return { upperLine, lowerLine, midLine }
  } else {
    // Standard channel based on two points
    return {
      upperLine: [startPoint, endPoint],
      lowerLine: [startPoint, endPoint] // Will be adjusted based on third point
    }
  }
}

// Default drawing styles
export const DEFAULT_DRAWING_STYLE: DrawingStyle = {
  lineColor: '#2962FF',
  lineWidth: 1,
  lineStyle: 'solid',
  textColor: '#E0E3EB',
  fontSize: 12,
  extend: {
    left: false,
    right: false
  }
}

// Color palette for drawings
export const DRAWING_COLORS = {
  primary: '#2962FF',
  success: '#4CAF50',
  warning: '#FF9800',
  error: '#F23645',
  info: '#2196F3',
  purple: '#9C27B0',
  gray: '#787B86',
  white: '#E0E3EB'
}

// Pattern Recognition Utilities
export const PATTERN_COLORS = {
  'head-and-shoulders': '#F23645',
  'double-top': '#FF9800',
  'double-bottom': '#4CAF50',
  'ascending-triangle': '#2196F3',
  'descending-triangle': '#F23645',
  'symmetrical-triangle': '#9C27B0',
  'rising-wedge': '#FF9800',
  'falling-wedge': '#4CAF50',
  'bull-flag': '#4CAF50',
  'bear-flag': '#F23645',
  'pennant': '#2962FF'
}

// Pattern validation utilities
export const validateHeadAndShoulders = (
  leftShoulder: PatternPoint,
  head: PatternPoint,
  rightShoulder: PatternPoint,
  tolerance: number = 0.1, // Increased tolerance to 10%
  isInverted: boolean = false
): boolean => {
  // For normal H&S: head should be higher than shoulders
  // For inverted H&S: head should be lower than shoulders
  const headCondition = isInverted 
    ? (head.price < leftShoulder.price && head.price < rightShoulder.price) // Inverted
    : (head.price > leftShoulder.price && head.price > rightShoulder.price) // Normal
  
  // Shoulders should be roughly equal (within tolerance)
  const shoulderDifference = Math.abs(leftShoulder.price - rightShoulder.price)
  const averageShoulderPrice = (leftShoulder.price + rightShoulder.price) / 2
  const shouldersEqual = shoulderDifference / averageShoulderPrice <= tolerance
  
  // Additional check: head should be significantly different from shoulders
  const leftHeadDiff = Math.abs(head.price - leftShoulder.price) / averageShoulderPrice
  const rightHeadDiff = Math.abs(head.price - rightShoulder.price) / averageShoulderPrice
  const headProminence = Math.min(leftHeadDiff, rightHeadDiff) > 0.02 // At least 2% difference
  
  return headCondition && shouldersEqual && headProminence
}

export const validateDoubleTopBottom = (
  firstPeak: PatternPoint,
  secondPeak: PatternPoint,
  tolerance: number = 0.03
): boolean => {
  // Peaks should be roughly equal (within tolerance)
  const priceDifference = Math.abs(firstPeak.price - secondPeak.price)
  const averagePrice = (firstPeak.price + secondPeak.price) / 2
  return priceDifference / averagePrice <= tolerance
}

export const calculateTriangleTrendlines = (
  highs: PatternPoint[],
  lows: PatternPoint[]
): { upperTrendline: [Point, Point], lowerTrendline: [Point, Point], convergencePoint: Point } => {
  if (highs.length < 2 || lows.length < 2) {
    throw new Error('Need at least 2 highs and 2 lows to form triangle')
  }
  
  // Calculate upper trendline (connecting highs)
  const firstHigh = highs[0]
  const lastHigh = highs[highs.length - 1]
  const upperSlope = (lastHigh.price - firstHigh.price) / (lastHigh.time - firstHigh.time)
  
  // Calculate lower trendline (connecting lows)
  const firstLow = lows[0]
  const lastLow = lows[lows.length - 1]
  const lowerSlope = (lastLow.price - firstLow.price) / (lastLow.time - firstLow.time)
  
  // Find convergence point
  const convergenceTime = (firstLow.price - firstHigh.price + upperSlope * firstHigh.time - lowerSlope * firstLow.time) / (upperSlope - lowerSlope)
  const convergencePrice = firstHigh.price + upperSlope * (convergenceTime - firstHigh.time)
  
  return {
    upperTrendline: [firstHigh, lastHigh],
    lowerTrendline: [firstLow, lastLow],
    convergencePoint: { time: convergenceTime, price: convergencePrice }
  }
}

export const calculateProjectedTarget = (
  patternType: DrawingType,
  patternHeight: number,
  breakoutPrice: number,
  isReversal: boolean = true
): number => {
  switch (patternType) {
    case 'head-and-shoulders':
    case 'double-top':
    case 'double-bottom':
      // Target = breakout price +/- pattern height
      return isReversal 
        ? breakoutPrice - patternHeight // Bearish reversal
        : breakoutPrice + patternHeight // Bullish reversal
    
    case 'ascending-triangle':
    case 'descending-triangle':
    case 'symmetrical-triangle':
      // Target = breakout price + pattern height in direction of breakout
      return breakoutPrice + patternHeight
    
    case 'rising-wedge':
    case 'falling-wedge':
      // Wedges are typically reversal patterns
      return isReversal 
        ? breakoutPrice - patternHeight 
        : breakoutPrice + patternHeight
    
    case 'bull-flag':
    case 'bear-flag':
    case 'pennant':
      // Continuation patterns: target = flagpole height projected from breakout
      return breakoutPrice + patternHeight
    
    default:
      return breakoutPrice
  }
}

export const detectPatternConfirmation = (
  patternType: DrawingType,
  breakoutPrice: number,
  volume: number,
  averageVolume: number,
  priceTarget: number
): boolean => {
  // Volume confirmation: breakout volume should be higher than average
  const volumeConfirmed = volume > averageVolume * 1.5
  
  // Price confirmation varies by pattern type
  let priceConfirmed = false
  
  switch (patternType) {
    case 'head-and-shoulders':
    case 'double-top':
      // Bearish confirmation: break below neckline
      priceConfirmed = breakoutPrice < priceTarget
      break
    
    case 'double-bottom':
      // Bullish confirmation: break above neckline
      priceConfirmed = breakoutPrice > priceTarget
      break
    
    case 'ascending-triangle':
    case 'bull-flag':
      // Bullish breakout above resistance
      priceConfirmed = breakoutPrice > priceTarget
      break
    
    case 'descending-triangle':
    case 'bear-flag':
      // Bearish breakdown below support
      priceConfirmed = breakoutPrice < priceTarget
      break
    
    case 'symmetrical-triangle':
    case 'rising-wedge':
    case 'falling-wedge':
    case 'pennant':
      // Breakout in either direction (distance from pattern bounds)
      priceConfirmed = Math.abs(breakoutPrice - priceTarget) > 0.02 * priceTarget
      break
    
    default:
      priceConfirmed = true
  }
  
  return volumeConfirmed && priceConfirmed
}

// Pattern quality scoring (0-1)
export const calculatePatternQuality = (
  patternType: DrawingType,
  points: PatternPoint[],
  duration: number,
  volume?: number[]
): number => {
  let score = 0.5 // Base score
  
  // Time factor: patterns need adequate time to develop
  const minDuration = 20 // Minimum candles for valid pattern
  const idealDuration = 50 // Ideal pattern duration
  
  if (duration < minDuration) {
    score -= 0.3
  } else if (duration > idealDuration) {
    score += 0.2
  }
  
  // Point significance: higher significance = better pattern
  const avgSignificance = points.reduce((sum, p) => sum + p.significance, 0) / points.length
  score += (avgSignificance - 0.5) * 0.4
  
  // Volume analysis (if available)
  if (volume && volume.length > 1) {
    const volumeTrend = volume[volume.length - 1] / volume[0]
    
    // Different patterns have different ideal volume characteristics
    switch (patternType) {
      case 'head-and-shoulders':
      case 'double-top':
      case 'double-bottom':
        // Should have declining volume towards completion
        if (volumeTrend < 0.8) score += 0.15
        break
      
      case 'rising-wedge':
      case 'falling-wedge':
        // Wedges typically have declining volume
        if (volumeTrend < 0.7) score += 0.2
        break
      
      case 'bull-flag':
      case 'bear-flag':
        // Flags should have lower volume during consolidation
        if (volumeTrend < 0.6) score += 0.1
        break
    }
  }
  
  // Ensure score stays within bounds
  return Math.max(0, Math.min(1, score))
} 